// frontend/src/components/BestTimeTool.js
//
// The personalised best-time-to-post tool, embedded on /best-time-to-post/*.
//
// Embedded rather than a standalone /tools/ page on purpose: `best time to post
// calculator` has ZERO keywords above the 500 bucket, so a separate URL would
// have nothing to rank for and would split signals off the article. The UTM
// builder got its own page because it had 10K-100K of its own demand; this does
// not.
//
// Calls POST /api/public/ai/best-time — unauthenticated, 3 per session. Separate
// from the authenticated in-app best-time route, which is untouched.

import React, { useState } from 'react'
import { Row, Col, Form, Button, Spinner } from 'react-bootstrap'
import { Stars, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons'

const PLATFORMS = [
  ['instagram', 'Instagram'], ['tiktok', 'TikTok'], ['facebook', 'Facebook'],
  ['linkedin', 'LinkedIn'], ['youtube', 'YouTube'], ['pinterest', 'Pinterest'],
  ['threads', 'Threads'], ['x', 'X'],
]
const CONTENT = [['photo', 'Mostly photos'], ['video', 'Mostly video'],
                 ['text', 'Mostly text'], ['mixed', 'A mix']]

/** Best-effort timezone/locale guess so the location field starts filled in. */
function guessLocation () {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    return tz.split('/').pop().replace(/_/g, ' ')
  } catch { return '' }
}

export default function BestTimeTool ({ platform = 'instagram' }) {
  const [form, setForm] = useState({
    business: '', platform, location: guessLocation(), contentType: 'mixed',
  })
  const [result, setResult]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [capped, setCapped]   = useState(false)

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    if (!form.business.trim()) { setError('Tell us what your business does first.'); return }
    setLoading(true); setError(''); setResult(null)
    try {
      const r = await fetch('/api/public/ai/best-time', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',          // the 3-per-session limit needs the cookie
        body: JSON.stringify(form),
      })
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'Something went wrong.'); setCapped(!!data.limitReached); return }
      setResult(data)
    } catch {
      setError('Could not reach the server. Try again in a moment.')
    } finally { setLoading(false) }
  }

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <Stars className='text-primary me-2' />
        Get your posting schedule
      </h2>
      <p className='text-muted mb-4'>
        Generic advice averages a bakery, a law firm and a gym together. Tell us what you
        actually do and get a week built for your audience — free, no account needed.
      </p>

      <Form onSubmit={submit}>
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold small'>What does your business do? <span className='text-danger'>*</span></Form.Label>
          <Form.Control
            as='textarea' rows={2} maxLength={300}
            placeholder='e.g. independent hair salon in Leeds, mostly women 25-45 booking cuts and colour'
            value={form.business} onChange={e => set('business', e.target.value)}
          />
          <Form.Text className='text-muted' style={{ fontSize: 12 }}>
            The more specific, the better the schedule. Who buys from you, and when are they free?
          </Form.Text>
        </Form.Group>

        <Row className='g-3 mb-3'>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Platform</Form.Label>
            <Form.Select value={form.platform} onChange={e => set('platform', e.target.value)}>
              {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Where are your customers?</Form.Label>
            <Form.Control placeholder='e.g. Leeds, UK' value={form.location}
                          onChange={e => set('location', e.target.value)} />
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>What do you post?</Form.Label>
            <Form.Select value={form.contentType} onChange={e => set('contentType', e.target.value)}>
              {CONTENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Select>
          </Col>
        </Row>

        <Button type='submit' variant='primary' size='lg' disabled={loading}>
          {loading
            ? <><Spinner size='sm' animation='border' className='me-2' />Building your week…</>
            : 'Build my posting schedule'}
        </Button>
        {!result && <span className='text-muted small ms-3'>Free · no sign-up · takes a few seconds</span>}
      </Form>

      {error && (
        <div className='mt-4 p-3 rounded-3 d-flex gap-2'
             style={{ background: capped ? '#eff6ff' : '#f8ecE8', border: `1px solid ${capped ? '#bfdbfe' : '#e8c4b8'}` }}>
          <ExclamationTriangleFill className={capped ? 'text-primary mt-1' : 'text-danger mt-1'} />
          <div>
            <div className='small'>{error}</div>
            {capped && (
              <Button size='sm' variant='primary' href='/register' className='mt-2'>
                Start using Greatly Social free
              </Button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className='mt-4'>
          {result.summary && <p className='fs-5 mb-3'>{result.summary}</p>}

          <div className='table-responsive mb-3'>
            <table className='table table-bordered align-middle mb-0'>
              <thead className='table-light'>
                <tr><th style={{ width: 110 }}>Day</th><th style={{ width: 150 }}>Post at</th><th>Why</th></tr>
              </thead>
              <tbody>
                {result.week.map(d => (
                  <tr key={d.day} style={{ opacity: d.slots?.length ? 1 : 0.55 }}>
                    <td className='fw-semibold'>{d.day}</td>
                    <td>{d.slots?.length
                      ? d.slots.map(s => <div key={s} className='fw-semibold text-primary'>{s}</div>)
                      : <span className='text-muted'>—</span>}</td>
                    <td className='text-muted small'>{d.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.tips?.length > 0 && (
            <div className='mb-3'>
              {result.tips.map(t => (
                <div className='d-flex align-items-start gap-2 mb-2' key={t}>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <span className='small'>{t}</span>
                </div>
              ))}
            </div>
          )}

          {result.verify && (
            <p className='text-muted small fst-italic'>{result.verify}</p>
          )}

          {/* The natural next step: they have a schedule and now need to act on it. */}
          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>Now put it on autopilot</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              At Greatly Social you can schedule these exact slots once and let them publish every
              week — to this platform and seven others, from one calendar.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2'>
              Start using Greatly Social free
            </Button>
            <span className='small' style={{ color: 'rgba(255,255,255,.75)' }}>
              14 days free · no card
            </span>
          </div>

          {typeof result.remaining === 'number' && (
            <p className='text-muted small mt-3 mb-0'>
              {result.remaining > 0
                ? `${result.remaining} more free schedule${result.remaining === 1 ? '' : 's'} this session.`
                : 'That was your last free schedule this session.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
